
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { resetPassword } from "@/services/auth.service";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { useToast } from "@/hooks/use-toast";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();
  const { theme } = useThemeConfig();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await resetPassword(email);
      setIsSuccess(true);
      toast({
        title: "Email inviata",
        description: "Controlla la tua email per reimpostare la password.",
        variant: "success"
      });
    } catch (error: any) {
      console.error("Errore nell'invio dell'email di reset:", error);
      toast({
        title: "Errore",
        description: "Impossibile inviare l'email di reset. Riprova più tardi.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div
      className="min-viewport-height overflow-y-auto flex flex-col p-6 pb-safe"
      style={{
        backgroundColor: theme.backgroundColor || '#f1f0fb',
        color: theme.textColor || '#000000'
      }}
    >
      <div className="mx-auto w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <img
              src="/logo.png"
              alt="Logo Ceralia"
              className="h-24 w-24 object-contain"
              draggable={false}
            />
          </div>
          <h1
            className="text-3xl font-bold"
            style={{ color: theme.sectionTitleColor }}
          >
            Ceralia - Spesa Semplice
          </h1>
          <p className="mt-2" style={{ color: theme.sectionTextColor }}>
            Recupero password
          </p>
        </div>
        
        <div 
          className="rounded-lg shadow-sm"
          style={{
            backgroundColor: theme.backgroundColor || '#FFFFFF',
            borderRadius: theme.borderRadius || '0.5rem',
            padding: theme.spacingBase || '1rem'
          }}
        >
          {isSuccess ? (
            <div className="text-center">
              <h2
                className="text-xl font-semibold mb-2"
                style={{ color: theme.sectionTitleColor }}
              >
                Email inviata!
              </h2>
              <p className="mb-4" style={{ color: theme.sectionTextColor }}>
                Abbiamo inviato un'email a {email} con le istruzioni per reimpostare la password.
              </p>
              <Button
                className="mt-4 w-full"
                style={{
                  backgroundColor: theme.primaryColor || '#4caf50',
                  borderRadius: theme.buttonBorderRadius,
                  padding: theme.buttonPadding
                }}
                asChild
              >
                <Link to="/login">Torna al login</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tuoemail@esempio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <Button
                type="submit"
                className="w-full"
                style={{
                  backgroundColor: theme.primaryColor || '#4caf50',
                  borderRadius: theme.buttonBorderRadius,
                  padding: theme.buttonPadding,
                  color: theme.textPrimaryColor,
                  fontWeight: 'bold',
                  transitionDuration: theme.transitionSpeed || '0.2s'
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Invio in corso..." : "Reimposta password"}
              </Button>
              
              <div className="text-center mt-4">
                <Link 
                  to="/login" 
                  style={{ 
                    color: theme.primaryColor || '#4caf50',
                    textDecoration: 'underline' 
                  }}
                >
                  Torna al login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
